import { FormulaError } from './errors.js';
import { parse } from './parser.js';
import type { ASTNode } from './parser.js';

export interface FieldInfo {
  slug: string;
  fieldType: string; // e.g. 'number', 'select', 'text', 'computed', etc.
}

/**
 * Collect all field_ref slugs from an AST.
 */
function collectFieldRefs(node: ASTNode): Set<string> {
  const refs = new Set<string>();

  function walk(n: ASTNode) {
    switch (n.type) {
      case 'field_ref':
        refs.add(n.slug);
        break;
      case 'binary':
        walk(n.left);
        walk(n.right);
        break;
      case 'unary':
        walk(n.operand);
        break;
      case 'call':
        for (const arg of n.args) {
          walk(arg);
        }
        break;
      // number, string, boolean — no refs
    }
  }

  walk(node);
  return refs;
}

/**
 * Validate a formula string.
 *
 * - Parses the formula (throws on syntax errors).
 * - Checks that all field references exist in availableFields.
 * - Ensures no self-reference.
 * - Ensures referenced fields are NOT computed (no computed→computed chains).
 *
 * Returns the list of referenced field slugs.
 */
export function validateFormula(
  formulaStr: string,
  availableFields: FieldInfo[],
  currentFieldSlug: string,
): string[] {
  // Parse
  const ast = parse(formulaStr);

  // Collect references
  const refs = collectFieldRefs(ast);
  const slugs = Array.from(refs);

  // Build lookup
  const fieldMap = new Map<string, FieldInfo>();
  for (const f of availableFields) {
    fieldMap.set(f.slug, f);
  }

  for (const slug of slugs) {
    // Self-reference check
    if (slug === currentFieldSlug) {
      throw new FormulaError(
        `Formula cannot reference itself (field '${slug}')`,
        'circular_dependency',
      );
    }

    // Existence check
    const field = fieldMap.get(slug);
    if (!field) {
      throw new FormulaError(
        `Unknown field reference: '${slug}'`,
        'unknown_field',
      );
    }

    // No computed→computed chains
    if (field.fieldType === 'computed') {
      throw new FormulaError(
        `Cannot reference computed field '${slug}' (computed→computed chains are not allowed)`,
        'invalid_formula',
      );
    }
  }

  return slugs;
}

/**
 * Check for circular dependencies among formulas.
 *
 * @param fieldSlug — the field to check
 * @param allFormulas — map of slug → list of dependency slugs for all computed fields
 * @returns true if a circular dependency exists
 */
export function checkCircularDeps(
  fieldSlug: string,
  allFormulas: Map<string, string[]>,
): boolean {
  const visited = new Set<string>();

  function dfs(slug: string): boolean {
    if (slug === fieldSlug && visited.size > 0) return true;
    if (visited.has(slug)) return false;
    visited.add(slug);

    const deps = allFormulas.get(slug);
    if (!deps) return false;

    for (const dep of deps) {
      if (dfs(dep)) return true;
    }

    return false;
  }

  const directDeps = allFormulas.get(fieldSlug);
  if (!directDeps) return false;

  for (const dep of directDeps) {
    if (dfs(dep)) return true;
  }

  return false;
}
